import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { toastPushed } from '@/features/ui/uiSlice'

export interface AsyncAction {
  /** Key of the action currently running, so one button spins on its own. */
  pending: string | null
  isPending: (key: string) => boolean
  run: (key: string, work: () => Promise<unknown>) => Promise<boolean>
}

/**
 * Mutations report success with their own toast; this adds the failure half and
 * the per-button pending flag. `work` should call `.unwrap()` so a rejected
 * thunk throws its message here.
 */
export function useAsyncAction(): AsyncAction {
  const dispatch = useAppDispatch()
  const [pending, setPending] = useState<string | null>(null)

  const run = useCallback(
    async (key: string, work: () => Promise<unknown>): Promise<boolean> => {
      setPending(key)
      try {
        await work()
        return true
      } catch (err) {
        dispatch(toastPushed(typeof err === 'string' ? err : 'Request failed', 'error'))
        return false
      } finally {
        setPending(null)
      }
    },
    [dispatch]
  )

  return { pending, isPending: (key) => pending === key, run }
}
